'use client'

import Link from 'next/link'
import { unsplashPlaceholderFor, type PlaceholderCategory } from '@/lib/copy/placeholder-images'
import { ArrowRight, Home, Sparkles } from 'lucide-react'

// Community Lost & Found 404 — instead of apologising for a broken link,
// we turn the dead end into a live wall of the kind of content Idukki
// exists to surface: real creator photos, products and UGC. The grid sits
// behind a legible navy scrim with a friendly message and a few real ways
// out. Pure CSS motion (gentle float), disabled for reduced-motion users.

const CATS: PlaceholderCategory[] = [
  'ugc', 'fashion', 'beauty', 'commerce', 'home', 'fashion',
  'ugc', 'beauty', 'commerce', 'fashion', 'ugc', 'home',
  'fashion', 'commerce', 'beauty', 'ugc', 'home', 'fashion',
]

const TILES = CATS.map((category, i) => ({
  src: unsplashPlaceholderFor(`404-${category}-${i}`, { category, width: 360, height: 360 }),
  // stagger the float so the wall feels alive rather than synchronised
  delay: `${(i % 6) * 0.6}s`,
  duration: `${6 + (i % 4)}s`,
}))

const QUICK_LINKS = [
  { label: 'Shoppable videos', href: '/shoppable-video' },
  { label: 'UGC widgets', href: '/widgets' },
  { label: 'Demo store', href: '/demo-store' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'The blog', href: '/blog' },
]

export function NotFoundCommunity() {
  return (
    <section className="relative isolate min-h-[88vh] overflow-hidden bg-brand-navy text-white">
      {/* Live community wall */}
      <div
        aria-hidden
        className="absolute inset-0 -z-20 grid grid-cols-3 gap-2 p-2 opacity-[0.38] sm:grid-cols-4 sm:gap-3 sm:p-3 md:grid-cols-6"
      >
        {TILES.map((t, i) => (
          <div
            key={i}
            className="not-found-tile overflow-hidden rounded-xl"
            style={{ animationDelay: t.delay, animationDuration: t.duration }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={t.src} alt="" loading="lazy" className="h-full w-full object-cover" />
          </div>
        ))}
      </div>

      {/* Scrim so the message stays readable over the wall */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(11,17,32,0.55)_0%,rgba(11,17,32,0.86)_55%,rgba(11,17,32,0.97)_100%)]"
      />

      <div className="relative mx-auto flex min-h-[88vh] max-w-2xl flex-col items-center justify-center px-6 py-20 text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-lime px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-brand-navy">
          <Sparkles className="h-3.5 w-3.5" /> 404
        </span>
        <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl text-balance">
          This page wandered off.
        </h1>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-white/80 sm:text-lg text-pretty">
          But look what the community is making right now. Real customer photos, videos and reviews,
          the exact kind of content Idukki turns into shoppable galleries.
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-lime px-5 py-2.5 text-sm font-semibold text-brand-navy transition hover:brightness-95"
          >
            <Home className="h-4 w-4" /> Back home
          </Link>
          <Link
            href="/demo-store"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
          >
            See it live <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-9">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/45">Popular destinations</p>
          <ul className="mt-3 flex flex-wrap items-center justify-center gap-2">
            {QUICK_LINKS.map((l) => (
              <li key={l.href}>
                <Link
                  href={l.href}
                  className="rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-sm text-white/85 transition hover:border-brand-lime/50 hover:text-white"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <style>{`
        .not-found-tile {
          animation-name: nfFloat;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
          will-change: transform;
        }
        @keyframes nfFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-8px) scale(1.015); }
        }
        @media (prefers-reduced-motion: reduce) {
          .not-found-tile { animation: none !important; }
        }
      `}</style>
    </section>
  )
}
